import React, { useEffect, useState } from "react";
import { message } from "antd";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Timer, ArrowRight, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Axios from "../../services/AxiosInterceptor";
import image from "../../img/eventHero.jpg";

const STORAGE_URL = "https://yogamatic.in/api/upload/";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0 },
};

const MainEventPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getImageUrl = (path) => {
    if (!path) return image;
    if (path.startsWith("http") || path.startsWith("https")) return path;
    return `${STORAGE_URL}${path}`;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await Axios.get("api/events_list");
        setEvents(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        message.error("Failed to load events");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const handleEventPage = (event) => {
    if (event?.status === "Expired") {
      message.info("This event has expired.");
      return;
    }
    navigate(`/Events_details/${event?._id}`);
  };

  return (
    <main className="ym-page">
      <section className="ym-hero" style={{ minHeight: 430 }}>
        <div className="ym-hero-media">
          <img src={image} alt="YogaMatic events" />
        </div>
        <div className="ym-hero-content" style={{ paddingTop: 72, paddingBottom: 80 }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <span className="ym-eyebrow">
              <CalendarDays size={14} />
              Workshops and events
            </span>
            <h1 className="ym-title">YogaMatic Events</h1>
            <p className="ym-lead">
              Join special yoga experiences, workshops, and community sessions curated for deeper practice.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="ym-section">
        <div className="ym-container">
          {loading && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "var(--ym-muted)" }}>
              Loading events...
            </div>
          )}

          {!loading && events.length > 0 && (
            <div className="ym-grid-3">
              {events.map((event, index) => {
                const expired = event.status === "Expired";
                const dateStr = event.date ? new Date(event.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Date TBA";
                return (
                  <motion.article
                    key={event._id}
                    className="ym-card"
                    style={{ overflow: "hidden", cursor: expired ? "not-allowed" : "pointer", opacity: expired ? 0.68 : 1, border: expired ? "1px solid var(--ym-line)" : "1px solid var(--ym-line)" }}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ delay: (index % 3) * 0.06 }}
                    whileHover={expired ? undefined : { y: -6, boxShadow: "var(--ym-shadow)" }}
                    onClick={() => handleEventPage(event)}
                  >
                    {/* Image */}
                    <div style={{ position: "relative", height: 220, overflow: "hidden" }}>
                      <img
                        src={getImageUrl(event.heroImage)}
                        alt={event.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover", filter: expired ? "grayscale(0.7)" : "none", transition: "transform 0.4s" }}
                        onMouseEnter={(e) => { if (!expired) e.currentTarget.style.transform = "scale(1.06)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                      />
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, rgba(23,32,27,0.35) 0%, transparent 50%)" }} />
                      <span
                        className="ym-pill"
                        style={{
                          position: "absolute",
                          top: 14,
                          left: 14,
                          background: expired ? "rgba(233,111,88,0.9)" : "rgba(34,160,107,0.9)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 11,
                        }}
                      >
                        {expired ? "Expired" : "Open"}
                      </span>
                    </div>

                    {/* Content */}
                    <div style={{ padding: "20px 18px" }}>
                      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 850, color: "var(--ym-ink)", lineHeight: 1.25 }}>{event.title}</h2>
                      {event.subtitle && (
                        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--ym-muted)", lineHeight: 1.4 }}>{event.subtitle}</p>
                      )}

                      <div style={{ display: "grid", gap: 8, marginTop: 16 }}>
                        <div style={{ display: "flex", gap: 9, color: "var(--ym-muted)", fontSize: 13, alignItems: "center" }}>
                          <CalendarDays size={15} style={{ flexShrink: 0, color: "var(--ym-gold)" }} />
                          <span style={{ fontWeight: 600 }}>{dateStr}</span>
                        </div>
                        <div style={{ display: "flex", gap: 9, color: "var(--ym-muted)", fontSize: 13, alignItems: "center" }}>
                          <Timer size={15} style={{ flexShrink: 0, color: "var(--ym-gold)" }} />
                          <span style={{ fontWeight: 600 }}>{event.time || "Time TBA"}</span>
                        </div>
                        <div style={{ display: "flex", gap: 9, color: "var(--ym-muted)", fontSize: 13, alignItems: "center" }}>
                          <MapPin size={15} style={{ flexShrink: 0, color: "var(--ym-gold)" }} />
                          <span style={{ fontWeight: 600 }}>{event.venue || "Venue TBA"}</span>
                        </div>
                      </div>

                      <div style={{ marginTop: 18, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid var(--ym-line)", paddingTop: 16 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--ym-muted)" }}>
                          <Users size={15} />
                          {event.availableSeats ?? "?"} seats
                        </span>
                        {!expired && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEventPage(event); }}
                            className="ym-button ym-button-gold"
                            style={{ padding: "6px 16px", minHeight: 0, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
                          >
                            View Event <ArrowRight size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}

          {!loading && events.length === 0 && (
            <div style={{ textAlign: "center", padding: "80px 24px" }}>
              <CalendarDays size={48} style={{ opacity: 0.2, marginBottom: 16, color: "var(--ym-muted)" }} />
              <h2 className="ym-title-small" style={{ margin: "0 0 12px" }}>No Events Available</h2>
              <p className="ym-lead" style={{ marginInline: "auto", maxWidth: 500 }}>
                We do not have upcoming events at the moment. Check back soon for new YogaMatic workshops.
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default MainEventPage;
